import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Typography, useMediaQuery,
} from "@mui/material";
import { api } from "./api";
import { Badge, ErrorBanner, GhostButton, OutlineButton, PreviewLock, StepActions, StepCard, SubHeading } from "./formParts";
import { Icon, T } from "./signupDesign";

export const slotKey = (s) => `${s.kind}:${s.ownerIndex == null ? "" : s.ownerIndex}`;

// Fallback when /meta hasn't said: the three proof-of-business documents,
// any one of which is enough.
const DEFAULT_GROUPS = {
  BUSINESS_PROOF: {
    label: "Proof of business",
    hint: "Upload any one: your business license, IRS EIN letter, or food service / health permit.",
    kinds: ["BUSINESS_LICENSE", "EIN_LETTER", "FOOD_PERMIT"],
  },
};

// The icon on each document's tile (display only).
const KIND_ICONS = {
  VOIDED_CHECK: Icon.Bank,
  OWNER_ID_FRONT: Icon.IdCard,
  OWNER_ID_BACK: Icon.IdCard,
  BUSINESS_LICENSE: Icon.Building,
  EIN_LETTER: Icon.FileText,
  FOOD_PERMIT: Icon.FileText,
  MENU: Icon.Menu,
};

// Which titled section a document is listed under (display only).
const IDENTITY_KINDS = ["VOIDED_CHECK", "OWNER_ID_FRONT", "OWNER_ID_BACK"];

function formatSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function IconTile({ done, kind }) {
  const KindIcon = KIND_ICONS[kind] || Icon.File;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
        background: done ? T.greenTint : T.redTint, color: done ? T.green : T.redText,
      }}
    >
      {done ? <Icon.Check size={22} /> : <KindIcon size={22} />}
    </Box>
  );
}

function Slot({ slot, files, busy, disabled, locked, onUpload, onDelete, inGroup }) {
  const done = files.length > 0;
  // Phones and tablets also get the camera, for a document they don't have
  // a picture or file of yet.
  const touch = useMediaQuery("(pointer: coarse)");
  // Which button the file came from, for its spinner.
  const [via, setVia] = useState("file");
  const pick = (source) => (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setVia(source);
    onUpload(slot, file);
  };
  const spinner = <CircularProgress size={16} sx={{ color: T.red }} />;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "44px minmax(0, 1fr)", sm: "44px minmax(0, 1fr) auto" },
        columnGap: 2,
        rowGap: 1.25,
        alignItems: "start",
        p: 2,
        mb: 1.5,
        borderRadius: "16px",
        border: `1px solid ${done ? "#CFE3CF" : T.cardBorder}`,
        background: done ? "#F7FBF7" : T.card,
      }}
    >
      <IconTile done={done} kind={slot.kind} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography component="h4" sx={{ fontWeight: 600, fontSize: 15.5, color: T.text }}>{slot.label}</Typography>
          {!inGroup && <Badge kind={slot.required ? "required" : "optional"}>{slot.required ? "Required" : "Optional"}</Badge>}
          {done && <Badge kind="done">Uploaded</Badge>}
        </Box>
        <Typography sx={{ color: T.hint, fontSize: 14, mt: 0.5, lineHeight: 1.5 }}>{slot.hint}</Typography>
      </Box>
      <Box
        sx={touch
          ? { gridColumn: { xs: "1 / -1", sm: "3" }, display: "flex", flexWrap: "wrap", gap: 1, "& > *": { flex: { xs: "1 1 0", sm: "0 0 auto" } } }
          : { gridColumn: { xs: "2", sm: "3" } }}
      >
        {touch && (
          <OutlineButton
            component="label"
            disabled={busy || disabled}
            startIcon={busy && via === "camera" ? spinner : <Icon.Camera size={18} />}
          >
            Take photo
            <input hidden type="file" accept="image/*" capture="environment" onChange={pick("camera")} />
          </OutlineButton>
        )}
        <OutlineButton
          component="label"
          disabled={busy || disabled}
          startIcon={busy && via === "file" ? spinner : <Icon.Upload size={18} />}
        >
          {done && !slot.multiple ? "Replace" : done ? "Add another" : "Upload"}
          <input
            hidden
            type="file"
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            onChange={pick("file")}
          />
        </OutlineButton>
      </Box>
      {files.length > 0 && (
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "2 / -1" } }}>
          {files.map((d) => (
            <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 44, borderTop: `1px solid ${T.divider}` }}>
              <Box sx={{ color: T.hint, display: "flex" }}><Icon.File size={18} /></Box>
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d.name} <span style={{ color: T.hint }}>· {formatSize(d.size)}</span>
              </Typography>
              {!locked && onDelete && (
                <IconButton aria-label={`Remove ${d.name}`} onClick={() => onDelete(d)} sx={{ width: 44, height: 44, color: T.muted }}>
                  <Icon.Trash size={18} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * The document slots under titled sections, with "any one of" groups boxed
 * together. Shared by the Documents step and the phone upload page.
 */
export function DocumentChecklist({ checklist, documents, groups, busySlot, disabled, locked, onUpload, onDelete }) {
  const allGroups = groups || DEFAULT_GROUPS;
  const filesFor = (slot) => documents.filter((d) => d.kind === slot.kind && (slot.ownerIndex == null || d.ownerIndex === slot.ownerIndex));
  const slotEl = (slot, inGroup) => (
    <Slot key={slotKey(slot)} slot={slot} files={filesFor(slot)} busy={busySlot === slotKey(slot)} disabled={disabled} locked={locked} onUpload={onUpload} onDelete={onDelete} inGroup={inGroup} />
  );

  // The same slots in the same order, collected into sections.
  const sections = [];
  const sectionFor = (key, make) => {
    let s = sections.find((x) => x.key === key);
    if (!s) {
      s = { key, ...make(), items: [] };
      sections.push(s);
    }
    return s;
  };
  const shownGroups = new Set();
  for (const slot of checklist) {
    const groupKey = slot.group;
    if (!groupKey || !allGroups[groupKey]) {
      const identity = IDENTITY_KINDS.includes(slot.kind);
      sectionFor(identity ? "identity" : "more", () => ({ title: identity ? "Bank & identity" : "Menu & other documents" })).items.push(slotEl(slot, false));
      continue;
    }
    if (shownGroups.has(groupKey)) continue;
    shownGroups.add(groupKey);
    const group = allGroups[groupKey];
    const members = checklist.filter((s) => s.group === groupKey);
    const satisfied = members.some((s) => s.uploaded > 0);
    sectionFor(`group-${groupKey}`, () => ({ title: group.label, group, satisfied })).items.push(
      <Box
        key={`group-${groupKey}`}
        sx={{ border: `1px dashed ${satisfied ? "#A9CFAB" : T.groupBorder}`, borderRadius: "18px", p: { xs: 1.25, sm: 2 }, pb: { xs: 0, sm: 0.5 }, background: satisfied ? "#F7FBF7" : T.groupBg }}
      >
        {members.map((m) => slotEl(m, true))}
      </Box>,
    );
  }

  return sections.map((s, i) => (
    <Box key={s.key} component="section" aria-label={s.title}>
      <SubHeading first={i === 0} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: s.group ? 0.75 : 2 }}>
        {s.title}
        {s.group && <Badge kind={s.satisfied ? "done" : "required"}>{s.satisfied ? "Done" : "Required: any one"}</Badge>}
      </SubHeading>
      {s.group && <Typography sx={{ color: T.hint, fontSize: 14, mb: 1.75 }}>{s.group.hint}</Typography>}
      {s.items}
    </Box>
  ));
}

/** QR code that opens a documents-only upload page on the applicant's phone. */
function PhoneUploadDialog({ open, token, onClose, onSaved }) {
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);
  // The parent passes a new onSaved every render; a ref keeps the effect
  // from re-running (and minting a new QR code) on each poll.
  const savedRef = useRef(onSaved);
  savedRef.current = onSaved;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLink(null);
    setError(null);
    api("POST", "/mobile-link", { token })
      .then((l) => alive && setLink(l))
      .catch((e) => alive && setError(e.message));
    // Pick up what arrives from the phone while the code is showing.
    const poll = setInterval(() => {
      api("GET", "/application", { token }).then((v) => alive && savedRef.current(v)).catch(() => {});
    }, 4000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, [open, token]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: T.display, fontWeight: 700, letterSpacing: "-0.02em", fontSize: 24, color: T.text }}>Upload from your phone</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
          {link ? (
            <Box component="img" alt="QR code to upload documents from your phone" src={`data:image/svg+xml;utf8,${encodeURIComponent(link.qrSvg)}`} sx={{ width: 220, height: 220 }} />
          ) : !error && <Box sx={{ width: 220, height: 220, display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress sx={{ color: T.red }} /></Box>}
        </Box>
        <Typography sx={{ color: T.text2, fontSize: 15, lineHeight: 1.6, textAlign: "center" }}>
          Scan with your phone's camera to take photos of your documents or pick files on your phone. They appear here as soon as they're uploaded.
        </Typography>
        <Typography sx={{ display: "block", color: T.hint, fontSize: 13, textAlign: "center", mt: 1.5 }}>
          The link works for 30 minutes and can only add documents to this application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GhostButton onClick={onClose}>Done</GhostButton>
      </DialogActions>
    </Dialog>
  );
}

function MetaItem({ icon, children }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: T.muted, fontSize: 14 }}>
      <Box component="span" sx={{ display: "flex", color: T.hint }}>{icon}</Box>
      {children}
    </Box>
  );
}

export default function DocumentsStep({ view, meta, token, preview, onSaved, onNext, onBack }) {
  const [busySlot, setBusySlot] = useState(null);
  const [error, setError] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const onPhone = useMediaQuery("(max-width:600px)");
  const locked = !preview && view.status !== "DRAFT";
  const groups = (meta && meta.documentGroups) || DEFAULT_GROUPS;

  const upload = async (slot, file) => {
    setError(null);
    if (file.size > 15 * 1024 * 1024) {
      setError("That file is over 15 MB.");
      return;
    }
    setBusySlot(slotKey(slot));
    try {
      const form = new FormData();
      form.append("kind", slot.kind);
      if (slot.ownerIndex != null) form.append("ownerIndex", String(slot.ownerIndex));
      form.append("file", file, file.name);
      onSaved(await api("POST", "/documents", { token, form }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusySlot(null);
    }
  };

  const remove = async (doc) => {
    setError(null);
    try {
      onSaved(await api("DELETE", `/documents/${doc.id}`, { token }));
    } catch (e) {
      setError(e.message);
    }
  };

  const docMissing = (view.missing || []).filter((m) => m.step === "documents");

  return (
    <StepCard
      title="Documents"
      subtitle="The payment processors' underwriting team needs these to open your merchant account."
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1, mt: 1.5, mb: 3 }}>
        <MetaItem icon={<Icon.File size={16} />}>PDF, JPG or PNG</MetaItem>
        <MetaItem icon={<Icon.Camera size={16} />}>A clear phone photo works if you don't have a scan</MetaItem>
      </Box>
      <ErrorBanner error={error} />
      {locked && <Alert severity="info" icon={<Icon.Info size={20} />} sx={{ mb: 2 }}>Your agreement is signed. You can still add documents our team asks for.</Alert>}
      {!preview && !onPhone && (
        <Box sx={{ p: 2, mb: 3, borderRadius: "16px", display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", background: T.footerBg, border: `1px solid ${T.cardBorder}` }}>
          <Box aria-hidden="true" sx={{ width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: T.redTint, color: T.redText }}>
            <Icon.Phone size={22} />
          </Box>
          <Typography sx={{ flex: "1 1 240px", color: T.text2, fontSize: 15 }}>Documents on your phone? Scan a QR code and upload them from there.</Typography>
          <OutlineButton onClick={() => setPhoneOpen(true)}>Upload from your phone</OutlineButton>
        </Box>
      )}
      <PreviewLock on={preview}>
        <DocumentChecklist
          checklist={view.checklist}
          documents={view.documents}
          groups={groups}
          busySlot={busySlot}
          disabled={preview}
          locked={locked}
          onUpload={upload}
          onDelete={remove}
        />
      </PreviewLock>
      <Typography sx={{ color: T.hint, fontSize: 14, mt: 1, display: "flex", gap: 0.75, alignItems: "flex-start" }}>
        <Box component="span" sx={{ mt: "2px", display: "flex" }}><Icon.Lock size={14} /></Box>
        <span>Files are encrypted and only RushMyFood's onboarding team and the processors see them.</span>
      </Typography>
      {!locked && !preview && docMissing.length > 0 && (
        <Typography sx={{ color: T.text2, fontSize: 14, mt: 2 }}>
          Still needed before you can sign: {docMissing.map((m) => m.message.replace(/\.$/, "")).join("; ")}. You can continue and come back to upload them.
        </Typography>
      )}
      <StepActions onBack={onBack} onNext={onNext} nextLabel="Continue" />
      {!preview && <PhoneUploadDialog open={phoneOpen} token={token} onClose={() => setPhoneOpen(false)} onSaved={onSaved} />}
    </StepCard>
  );
}
